import { useState, useEffect, useCallback } from 'react';
import { DayCancellationsSection } from './DayCancellationsSection';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Printer, RefreshCw, Banknote, CreditCard, Receipt, Package, MessageCircle, LogOut, Moon, Mail, FileText, UtensilsCrossed, Building2, Smartphone, Users } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Last 7 days' },
  { key: 'month', label: 'Last 30 days' },
];

const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function DayReportDialog({ open, onOpenChange, branchId, onPrint, closeout = false, onLogout }) {
  const [range, setRange] = useState('today');
  const [view, setView] = useState('sales');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [waPhone, setWaPhone] = useState(() => localStorage.getItem('day_summary_wa_phone') || '');
  const [waEmail, setWaEmail] = useState(() => localStorage.getItem('day_summary_email') || '');
  const [sendingWa, setSendingWa] = useState(false);
  const [ccOwners, setCcOwners] = useState(false);
  const [attachPdf, setAttachPdf] = useState(() => localStorage.getItem('day_summary_attach_pdf') !== '0');

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('cashier_token');
      const today = new Date();
      let params = { branch_id: branchId || undefined };
      if (r === 'week') {
        const lo = new Date(today); lo.setDate(today.getDate() - 6);
        params = { ...params, start_date: toYmd(lo), end_date: toYmd(today) };
      } else if (r === 'month') {
        const lo = new Date(today); lo.setDate(today.getDate() - 29);
        params = { ...params, start_date: toYmd(lo), end_date: toYmd(today) };
      }
      const { data: d } = await api.get('/cashier/day-report', {
        params, headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load summary');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { if (open) load(range); }, [open, range, load]);

  const sym = data?.currency || 'SAR';
  const t = data?.totals || {};
  const money = (v) => `${sym} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const sendToWhatsApp = async () => {
    const phone = waPhone.trim();
    const email = waEmail.trim();
    if (!phone && !email && !ccOwners) return toast.error('Enter a WhatsApp number or email, or tick "send to owner/managers"');
    if (!data) return;
    setSendingWa(true);
    try {
      if (phone) localStorage.setItem('day_summary_wa_phone', phone);
      if (email) localStorage.setItem('day_summary_email', email);
      localStorage.setItem('day_summary_attach_pdf', attachPdf ? '1' : '0');
      const token = localStorage.getItem('cashier_token');
      const res = await api.post('/whatsapp-digests/send-day-summary', {
        phone,
        email,
        cc_recipients: ccOwners,
        attach_pdf: attachPdf,
        branch_id: branchId || null,
        summary: {
          title: data?.branch?.name || 'POS',
          period_label: data?.is_range ? `${data.period_start} → ${data.period_end}` : (data?.date || 'Today'),
          currency: sym,
          order_count: data.order_count,
          totals: t,
          top_items: data.top_items || [],
        },
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success(res.data?.message || 'Sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingWa(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88dvh] overflow-y-auto" data-testid="day-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-indigo-600" /> Day Summary
          </DialogTitle>
          <DialogDescription>
            {data?.branch?.name ? `${data.branch.name} · ` : ''}
            {data?.is_range ? `${data.period_start} → ${data.period_end} (${data.days_covered} days)` : (data?.date || 'Today')}
          </DialogDescription>
        </DialogHeader>

        {closeout && (
          <div className="flex items-start gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2.5 text-[13px] text-indigo-800" data-testid="day-summary-closeout-banner">
            <Moon className="w-4 h-4 mt-0.5 shrink-0" />
            <span>End of shift — review today's totals, send them to WhatsApp if you like, then log out.</span>
          </div>
        )}

        {/* Range toggle */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {RANGES.map((r) => (
            <Button
              key={r.key}
              size="sm"
              variant={range === r.key ? 'default' : 'outline'}
              className="h-8"
              onClick={() => setRange(r.key)}
              data-testid={`day-summary-range-${r.key}`}
            >
              {r.label}
            </Button>
          ))}
          <Button size="icon" variant="ghost" className="h-8 w-8 ml-auto" onClick={() => load(range)} data-testid="day-summary-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="flex gap-1.5" data-testid="day-summary-tabs">
          <Button size="sm" className="h-10 flex-1" variant={view === 'sales' ? 'default' : 'outline'} onClick={() => setView('sales')} data-testid="day-summary-tab-sales">Sales</Button>
          <Button size="sm" className="h-10 flex-1" variant={view === 'cancellations' ? 'default' : 'outline'} onClick={() => setView('cancellations')} data-testid="day-summary-tab-cancellations">
            Cancellations &amp; Voids
          </Button>
        </div>

        {view === 'cancellations' ? (
          <DayCancellationsSection data={data?.cancellations} label={data?.date || range} />
        ) : loading && !data ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : !data ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No data</div>
        ) : (
          <div className="space-y-4" data-testid="day-summary-body">
            {/* Payment split — distinct Cash / Bank / Card / Online / Credit.
                Reuses the order payment_details the backend already split; shown
                consistently (even at 0) so closing/reconciliation is clear. */}
            <div className="grid grid-cols-3 gap-2" data-testid="day-summary-payment-split">
              {[
                { key: 'cash', label: 'Cash', Icon: Banknote, cls: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
                { key: 'bank', label: 'Bank', Icon: Building2, cls: 'bg-sky-50 border-sky-200 text-sky-800' },
                { key: 'card', label: 'Card', Icon: CreditCard, cls: 'bg-blue-50 border-blue-200 text-blue-800' },
                { key: 'online', label: 'Online', Icon: Smartphone, cls: 'bg-purple-50 border-purple-200 text-purple-800' },
                { key: 'credit', label: 'Credit', Icon: Users, cls: 'bg-amber-50 border-amber-200 text-amber-800' },
              ].map(({ key, label, Icon, cls }) => (
                <div key={key} className={`rounded-xl border p-2.5 ${cls}`} data-testid={`day-summary-${key}`}>
                  <div className="flex items-center gap-1 text-[11px] font-medium"><Icon className="w-3.5 h-3.5" /> {label}</div>
                  <div className="text-base font-bold mt-0.5 tabular-nums">{money(t[key])}</div>
                </div>
              ))}
            </div>
            <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50 p-3 flex items-center justify-between" data-testid="day-summary-total">
              <div>
                <div className="text-[11px] text-indigo-700 font-medium">TOTAL SALES</div>
                <div className="text-2xl font-bold text-indigo-900">{money(t.total)}</div>
              </div>
              <div className="text-right text-xs text-indigo-700">
                <div><span className="font-semibold">{data.order_count}</span> orders</div>
                {data.refund_count ? <div className="text-rose-600">{data.refund_count} refunded</div> : null}
                {data.is_range ? <div>Avg/day {money((t.total || 0) / Math.max(data.days_covered, 1))}</div> : null}
              </div>
            </div>

            {/* Breakdown */}
            <div className="rounded-xl border p-3 space-y-1 text-sm" data-testid="day-summary-breakdown">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{money(t.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>{money(t.tax)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>- {money(t.discount)}</span></div>
            </div>

            {/* iter964 — Staff Food + Cash Drawer, shown SEPARATELY from sales. */}
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl border bg-amber-50 border-amber-200 p-3" data-testid="day-summary-staff-food">
                <div className="flex items-center gap-1.5 text-[11px] text-amber-700 font-medium"><UtensilsCrossed className="w-3.5 h-3.5" /> Staff Food (not a sale)</div>
                <div className="text-lg font-bold text-amber-900 mt-0.5">{money(data.staff_food?.value || 0)}</div>
                <div className="text-[11px] text-amber-700">{data.staff_food?.qty || 0} unit(s) · {data.staff_food?.count || 0} order(s)</div>
              </div>
              <div className="rounded-xl border bg-slate-50 border-slate-200 p-3" data-testid="day-summary-drawer">
                <div className="flex items-center gap-1.5 text-[11px] text-slate-700 font-medium"><Banknote className="w-3.5 h-3.5" /> Cash Drawer Openings</div>
                <div className="text-lg font-bold text-slate-900 mt-0.5">{data.drawer_openings?.total || 0}</div>
                <div className="text-[11px] text-slate-600">{data.drawer_openings?.cash_sale || 0} cash sale · {data.drawer_openings?.manual || 0} manual</div>
              </div>
            </div>

            {/* Items sold */}
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                <Package className="w-3.5 h-3.5" /> Items Sold (top {(data.top_items || []).length})
              </div>
              {(data.top_items || []).length === 0 ? (
                <div className="text-sm text-muted-foreground py-2">No items sold in this period.</div>
              ) : (
                <div className="rounded-xl border divide-y" data-testid="day-summary-items">
                  {(data.top_items || []).map((it, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`day-summary-item-${i}`}>
                      <span className="truncate"><span className="font-semibold text-indigo-600">{it.qty}×</span> {it.name}</span>
                      <span className="font-medium tabular-nums">{money(it.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* By category */}
            {(data.by_category || []).length > 0 && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">By Category</div>
                <div className="rounded-xl border divide-y" data-testid="day-summary-categories">
                  {(data.by_category || []).slice(0, 8).map((c, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-[13px]">
                      <span className="truncate text-muted-foreground">{c.name} ({c.qty})</span>
                      <span className="tabular-nums">{money(c.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2.5 pt-1 rounded-xl border border-dashed border-stone-300 p-3" data-testid="day-summary-send-panel">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Send this summary (all optional)</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <MessageCircle className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-emerald-600" />
                  <Input
                    value={waPhone}
                    onChange={(e) => setWaPhone(e.target.value)}
                    placeholder="WhatsApp +9665XXXXXXXX"
                    className="h-10 pl-8"
                    data-testid="day-summary-wa-phone"
                  />
                </div>
              </div>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-sky-600" />
                <Input
                  type="email"
                  value={waEmail}
                  onChange={(e) => setWaEmail(e.target.value)}
                  placeholder="Email (optional)"
                  className="h-10 pl-8"
                  data-testid="day-summary-email"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none" data-testid="day-summary-attach-pdf-label">
                <input
                  type="checkbox"
                  className="rounded border-input"
                  checked={attachPdf}
                  onChange={(e) => setAttachPdf(e.target.checked)}
                  data-testid="day-summary-attach-pdf"
                />
                <FileText className="w-3.5 h-3.5 text-rose-600" />
                Attach PDF Z-report
              </label>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="rounded border-input"
                  checked={ccOwners}
                  onChange={(e) => setCcOwners(e.target.checked)}
                  data-testid="day-summary-cc-owners"
                />
                Also send to this branch's owner &amp; manager (from WhatsApp Digests)
              </label>
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={sendingWa}
                onClick={sendToWhatsApp}
                data-testid="day-summary-send-wa"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> {sendingWa ? 'Sending…' : 'Send summary'}
              </Button>
            </div>

            <Button className="w-full" onClick={() => onPrint?.(range)} data-testid="day-summary-print">
              <Printer className="w-4 h-4 mr-2" /> Print Z-Report
            </Button>

            {closeout && (
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={() => { onOpenChange?.(false); onLogout?.(); }}
                data-testid="day-summary-logout"
              >
                <LogOut className="w-4 h-4 mr-2" /> Log Out
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DayReportDialog;
